import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";

/**
 * Catches a render/lifecycle throw inside a route and shows a fallback card instead of blanking the
 * whole shell. Keyed on the pathname by the wrapper below, so navigating away resets it.
 */
class Boundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[SunBiz] route "${this.props.route}" crashed:`, error, info?.componentStack);
    try { if (window.gtag) window.gtag("event", "exception", { description: `${this.props.route}: ${error?.message || error}`.slice(0, 150), fatal: false }); } catch { /* ignore */ }
  }
  render() {
    if (!this.state.error) return this.props.children;
    const { error } = this.state;
    return (
      <div data-testid="route-error-fallback" style={{ maxWidth: 640, margin: "48px auto", padding: 28, borderRadius: 16, background: "#fff", border: "1px solid #FECACA", boxShadow: "0 10px 30px rgba(15,42,29,0.08)", fontFamily: "Manrope, sans-serif", color: "#0F2A1D" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center" }}><AlertTriangle size={20} color="#DC2626" /></div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, fontFamily: "Outfit, sans-serif" }}>This page hit an error</h2>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 14, color: "#4A5D52" }}>The rest of SunBiz is fine — only this screen failed to render. The error has been logged.</p>
        <pre data-testid="route-error-message" style={{ margin: "10px 0 16px", padding: "10px 12px", borderRadius: 10, background: "#F8FAFC", border: "1px solid #E2E8F0", fontSize: 12, color: "#64748B", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(error?.message || error)}</pre>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link to="/app/dashboard" data-testid="route-error-dashboard" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 100, background: "#2F7D52", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 14 }}><ArrowLeft size={15} /> Back to dashboard</Link>
          <button type="button" onClick={() => window.location.reload()} data-testid="route-error-reload" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 100, background: "#fff", color: "#2F7D52", border: "1px solid rgba(47,125,82,0.3)", fontWeight: 600, fontSize: 14, cursor: "pointer" }}><RefreshCw size={15} /> Reload</button>
        </div>
      </div>
    );
  }
}

/** Diagnostic: append `?__crash=1` to any route to throw inside it and prove the boundary holds. */
function CrashProbe() {
  const { search } = useLocation();
  if (/[?&]__crash=1(&|$)/.test(search)) throw new Error("Deliberate test crash (__crash=1)");
  return null;
}

export default function RouteErrorBoundary({ children, probe = false }) {
  const { pathname } = useLocation();
  return <Boundary key={pathname} route={pathname}>{probe && <CrashProbe />}{children}</Boundary>;
}
